import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { basicInfoQuestions, allowancesQuestions, exemptionQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function OklahomaForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formatFullName,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const allowancesForm = useRef();
  const [allowanceSelf, setAllowanceSelf] = useState(data.allowance_self || false);
  const allowancesInitQ = getInitialQuestions(allowancesQuestions);
  const [allowances, setAllowances] = useState(allowancesInitQ);

  const [allowanceSpouse, setAllowanceSpouse] = useState(data.allowance_spouse || false);

  const exemptionsForm = useRef();
  const exemptionsInitQ = getInitialQuestions(exemptionQuestions);
  const [exemptions, setExemptions] = useState(exemptionsInitQ);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowanceSpouse = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired={basicInfo[8].answer.answer.filing_status !== 'single'}>
        Allowance for your spouse: Does your spouse
        {' '}
        {'\n'}
        work?
      </S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={allowanceSpouse}
              checked={allowanceSpouse}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setAllowanceSpouse(!allowanceSpouse);
              }}
            />
            <Box ml={2} />
            Yes
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={!allowanceSpouse}
              checked={!allowanceSpouse}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setAllowanceSpouse(!allowanceSpouse);
              }}
            />
            <Box ml={2} />
            No
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAllowancesForm = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontSize: '16px', fontWeight: '600' }}>
        Allowances
      </Text>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="checkbox"
              value={allowanceSelf}
              checked={allowanceSelf}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setAllowanceSelf(e.target.checked);
              }}
            />
            <Box ml={2} />
            Allowance for yourself: Check this box
          </Flex>
        </label>
      </S.QuestionWrapper>
      {basicInfo[8].answer.answer.filing_status !== 'single' && renderAllowanceSpouse(isReviewStep)}
      <FormConsumer
        questions={allowances}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowances.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowances(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderExemptionsForm = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontSize: '16px', fontWeight: '600' }}>
        Exemption from withholding
      </Text>
      <FormConsumer
        questions={exemptions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptions(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const calculateTotalAllowances = (allowanceSpouseTemp) => {
    let res = 0;

    if (allowanceSelf) res += 1;
    if (allowanceSpouseTemp !== '' && !allowanceSpouseTemp) res += 1;

    res += parseInt(allowances[0].answer.answer.dependents) || 0;
    res += parseInt(allowances[1].answer.answer.additional_allowances) || 0;

    return res;
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {isExemptFromWithholding && renderExemptionsForm()}
          {!isExemptFromWithholding && renderAllowancesForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              // - save answers to server
              // - processed to next step
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const exemptionStatusData = { is_exempt: isExemptFromWithholding };

              let exemptionData;

              let allowanceSelfData;
              let allowanceSpouseTemp = '';
              let allowancesData;
              let numAllowancesTemp = '';

              if (isExemptFromWithholding) {
                exemptionData = exemptions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                allowanceSelfData = { allowance_self: '' };
                allowancesData = resetInitialQuestions(allowancesQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              } else {
                exemptionData = resetInitialQuestions(exemptionQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                allowanceSelfData = { allowance_self: allowanceSelf };

                if (basicInfo[8].answer.answer.filing_status !== 'single') {
                  allowanceSpouseTemp = allowanceSpouse;
                }

                allowancesData = allowances.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                numAllowancesTemp = calculateTotalAllowances(allowanceSpouseTemp).toString();
              }

              const allowanceSpouseData = { allowance_spouse: allowanceSpouseTemp };
              const numAllowancesData = { total_num_allowances: numAllowancesTemp };
              const allAllowancesData = {
                ...allowanceSelfData, ...allowanceSpouseData, ...allowancesData, ...numAllowancesData,
              };
              const data = {
                ...basicInfoData, ...exemptionStatusData, ...exemptionData, ...allAllowancesData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {isExemptFromWithholding && renderExemptionsForm(true)}
        {!isExemptFromWithholding && renderAllowancesForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default OklahomaForm;
